/**
 * Hook for getting actions from the registry.
 *
 * This replaces the old useActions hook with registry-based action management.
 */

import { useMemo, useRef } from "react";
import Fuse from "fuse.js";
import { ActionCategory, ActionContext, ActionWithShortcut, getActionCategoryOrder } from "./types";
import { actionRegistry, initializeRegistry } from "./ActionRegistry";

// Import all definitions and factories

import { useActionDeps } from "./useActionDeps";

/**
 * Hook that returns actions grouped by category.
 *
 * This is the main hook for getting actions to display in the menu.
 */
export function useRegisteredActions(context: ActionContext): {
  actions: {
    category: ActionCategory;
    actions: ActionWithShortcut[];
  }[];
  allActions: ActionWithShortcut[];
} {
  // Initialize registry on first render
  const initRef = useRef(false);
  if (!initRef.current) {
    initializeRegistry();
    initRef.current = true;
  }

  // Get performers
  const actionDeps = useActionDeps();

  // Get actions from registry that are valid for the current context
  const allActionsBase = useMemo(() => {
    const actions = actionRegistry.getActions(context, actionDeps) as ActionWithShortcut[];

    // Filter based on context (same logic as old useActions)
    return actions.filter(action => action.enabled);
  }, [context, actionDeps]);

  // Set up fuzzy search
  const fuse = useMemo(
    () =>
      new Fuse(allActionsBase, {
        keys: [
          { name: "label", weight: 0.7 },
          { name: "synonyms", weight: 0.3 },
        ],
        threshold: 0.3,
        shouldSort: true,
        findAllMatches: true,
        includeScore: true,
      }),
    [allActionsBase]
  );

  // Apply search/filter and group by category
  return useMemo(() => {
    let allActions: ActionWithShortcut[];
    const userInput = context.userInput?.trim();
    const hasUserInput = Boolean(userInput.length);

    if (userInput === "**") {
      // Secret shortcut for accessing all available actions.
      allActions = allActionsBase;
    } else if (hasUserInput) {
      // Search with fuzzy matching
      const results = fuse.search(context.userInput);
      allActions = results.map(result => result.item);
    } else {
      // Show default actions only
      allActions = allActionsBase.filter(action => action.default);
    }

    // Group by category
    const byCategory = allActions.reduce(
      (acc, action) => {
        if (!acc[action.category]) {
          acc[action.category] = [];
        }
        acc[action.category].push(action);
        return acc;
      },
      {} as Record<ActionCategory, ActionWithShortcut[]>
    );

    // Sort categories and return
    const actions = Object.entries(byCategory)
      .map(([category, actions]) => ({
        category: category as ActionCategory,
        actions,
      }))
      .sort((a, b) =>
        !hasUserInput ? getActionCategoryOrder(a.category) - getActionCategoryOrder(b.category) : 0
      );
    return {
      actions,
      allActions: allActionsBase,
    };
  }, [allActionsBase, context.userInput, fuse]);
}

/**
 * Hook for getting a single action by ID.
 */
export function useActionById(actionId: string, context: ActionContext): ActionWithShortcut | null {
  // Initialize registry
  const initRef = useRef(false);
  if (!initRef.current) {
    initializeRegistry();
    initRef.current = true;
  }

  const actionDeps = useActionDeps();

  return useMemo(() => {
    return actionRegistry.getActionById(actionId, context, actionDeps) as ActionWithShortcut | null;
  }, [actionId, context, actionDeps]);
}

// Re-export the registry for direct access if needed
// export { actionRegistry, initializeRegistry };
